import { useEffect, useId, useLayoutEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/** The focusable controls inside `container`, in DOM order (the trap's first and last control). */
export function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

/** Whether `el` is drawn: no `hidden`, `display: none` or `visibility: hidden` up to `container`. */
function isRendered(el: HTMLElement, container: HTMLElement): boolean {
  if (typeof el.checkVisibility === "function") {
    return el.checkVisibility({ visibilityProperty: true, checkVisibilityCSS: true } as CheckVisibilityOptions);
  }
  for (let node: HTMLElement | null = el; node; node = node === container ? null : node.parentElement) {
    if (node.hidden || getComputedStyle(node).display === "none") return false;
  }
  return getComputedStyle(el).visibility !== "hidden";
}

/**
 * The controls the Tab key actually stops at inside `container`, in DOM
 * order: the focusable ones minus those out of the sequence (`tabindex="-1"`:
 * the options of a roving group past its stop), inert or not drawn
 * (`hidden`, `display: none`). Where a Popover's first and last stops are
 * (q-ui-package-01): one of those after the real last stop would let the Tab
 * leave for the end of <body> with the panel still open.
 */
export function getTabStops(container: HTMLElement): HTMLElement[] {
  return getFocusableElements(container).filter(
    (el) => el.tabIndex >= 0 && !el.closest("[inert]") && isRendered(el, container),
  );
}

/** Whether `element` shows inside every scrolling box between it and `root`, without scrolling any. */
function isInScrollView(element: HTMLElement, root: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  for (let box = element.parentElement; box; box = box.parentElement) {
    if (box.scrollHeight > box.clientHeight) {
      const overflowY = getComputedStyle(box).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") {
        const view = box.getBoundingClientRect();
        if (rect.top < view.top || rect.bottom > view.bottom) return false;
      }
    }
    if (box === root) break;
  }
  return true;
}

/**
 * Where an overlay puts the focus when it opens (thread-02): its first control
 * when that control is already on screen, otherwise the dialog itself. A
 * reading sheet (the negotiation guide, the practice) opens at its summary,
 * never scrolled down to a «Copiar» halfway through; the next Tab still lands
 * on the first control. Measured at once: where there is no
 * IntersectionObserver (jsdom); `focusOnOpen` reads nothing on open.
 */
export function initialFocusTarget(root: HTMLElement, dialog: HTMLElement | null): HTMLElement {
  const first = getFocusableElements(root)[0];
  if (!first || !dialog) return first ?? dialog ?? root;
  if (isInScrollView(first, root)) return first;
  return dialog;
}

/**
 * Puts the focus in an overlay that opens, without reading layout in the
 * commit that mounts it (that read would restyle and lay out the whole page
 * there and then). The dialog takes the focus at once; its first control
 * takes it over once a frame shows that control whole inside the overlay
 * (an IntersectionObserver rooted there, so a sheet's slide-in moves both
 * alike), unless the person moved it meanwhile. Returns the cleanup.
 */
function focusOnOpen(root: HTMLElement, dialog: HTMLElement | null): () => void {
  const first = getFocusableElements(root)[0];
  if (!first || !dialog || typeof IntersectionObserver !== "function") {
    initialFocusTarget(root, dialog).focus({ preventScroll: true });
    return () => {};
  }
  dialog.focus({ preventScroll: true });
  const watch = new IntersectionObserver(
    ([entry]) => {
      watch.disconnect();
      if (!entry?.isIntersecting || entry.intersectionRect.height < entry.boundingClientRect.height - 1) return;
      if (document.activeElement === dialog && first.isConnected) first.focus({ preventScroll: true });
    },
    { root },
  );
  watch.observe(first);
  return () => watch.disconnect();
}

/*
 * Scroll lock and key handling are stacked so nested overlays (e.g. a Dialog
 * opened from inside a Sheet) don't release each other's lock or fight over focus.
 */
const overlayStack: number[] = [];
let scrollLockCount = 0;
let savedBodyOverflow = "";

function lockBodyScroll() {
  if (scrollLockCount === 0) {
    savedBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  scrollLockCount += 1;
}

function unlockBodyScroll() {
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  if (scrollLockCount === 0) {
    document.body.style.overflow = savedBodyOverflow;
  }
}

let nextOverlayId = 0;

/**
 * `<html data-modal-open>` while a modal overlay is open (spec 07 ›
 * Rendimiento, ux-a11y-perf-03): the CSS reads the state of the document as
 * `:root[data-modal-open]` (the toasts go to the top, a plain toast steps
 * aside while typing in one), never as `body:has([role="dialog"][aria-modal])`,
 * which the engine tested again on every insertion in the page. Counted, so a
 * Dialog opened from a Sheet keeps it until the last one closes; written in
 * the commit that opens or closes it, before it paints.
 */
const MODAL_OPEN_ATTRIBUTE = "data-modal-open";
let openModals = 0;

function holdModalOpen(): () => void {
  openModals += 1;
  if (openModals === 1) document.documentElement.setAttribute(MODAL_OPEN_ATTRIBUTE, "");
  return () => {
    openModals = Math.max(0, openModals - 1);
    if (openModals === 0) document.documentElement.removeAttribute(MODAL_OPEN_ATTRIBUTE);
  };
}

/*
 * Inert background (a11y-20): while a modal is open, everything outside it is
 * `inert`, so a screen reader that ignores `aria-modal` cannot swipe out of
 * the sheet and a click or Tab never reaches the page behind. Like Radix's
 * `hideOthers`, the walk goes down from <body> and only makes inert the
 * branches that hold nothing to keep: the overlay's own nodes and the layers
 * that must stay live above any modal (`[data-overlay-keep]`: the toast
 * region, whose live regions would go silent inside an inert subtree).
 * Counted per element so nested overlays release only their own marks, and an
 * element that was already inert before is never touched.
 */
const inertCounts = new Map<Element, number>();
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "LINK", "TEMPLATE", "NOSCRIPT"]);

function collectInertTargets(keep: Element[]): Element[] {
  const targets: Element[] = [];
  const walk = (parent: Element) => {
    for (const child of Array.from(parent.children)) {
      if (SKIP_TAGS.has(child.tagName)) continue;
      if (keep.some((node) => node === child)) continue;
      if (keep.some((node) => child.contains(node))) {
        walk(child);
        continue;
      }
      targets.push(child);
    }
  };
  walk(document.body);
  return targets;
}

function markInert(targets: Element[]): Element[] {
  const marked: Element[] = [];
  for (const element of targets) {
    const count = inertCounts.get(element) ?? 0;
    if (count === 0 && element.hasAttribute("inert")) continue;
    if (count === 0) element.setAttribute("inert", "");
    inertCounts.set(element, count + 1);
    marked.push(element);
  }
  return marked;
}

function releaseInert(marked: Element[]) {
  for (const element of marked) {
    const count = (inertCounts.get(element) ?? 1) - 1;
    if (count <= 0) {
      inertCounts.delete(element);
      element.removeAttribute("inert");
    } else {
      inertCounts.set(element, count);
    }
  }
}

/** Attribute that ties an overlay's parts outside its root (the Sheet scrim) to it. */
export const OVERLAY_PART_ATTRIBUTE = "data-overlay-part";

interface UseOverlayBehaviorOptions {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Shared modal behavior for Dialog/Sheet: body scroll lock, inert background,
 * `<html data-modal-open>`, Escape-to-close, Tab focus trap, initial focus, and
 * focus restoration on close.
 * Returns a ref for the element wrapping the overlay's focusable content
 * (the backdrop container for Dialog, the panel itself for Sheet).
 */
export function useOverlayBehavior({ open, onOpenChange }: UseOverlayBehaviorOptions) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const onOpenChangeRef = useRef(onOpenChange);
  // Marks the overlay's parts that live outside `rootRef` (`data-overlay-part`).
  const partId = useId();

  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  });

  useLayoutEffect(() => {
    if (!open) return;
    return holdModalOpen();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const overlayId = ++nextOverlayId;
    overlayStack.push(overlayId);

    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const root = rootRef.current;
    // The focus first: focusing brings the style up to date, and before the
    // page behind turns inert (which restyles all of it) that is only what
    // this commit mounted; the page's restyle waits for the frame.
    // preventScroll: focusing never moves the content (thread-02).
    const stopFocus = root
      ? focusOnOpen(
          root,
          root.getAttribute("role") === "dialog" ? root : root.querySelector<HTMLElement>('[role="dialog"]'),
        )
      : () => {};

    lockBodyScroll();
    const keep: Element[] = [
      ...(root ? [root] : []),
      ...Array.from(document.querySelectorAll(`[${OVERLAY_PART_ATTRIBUTE}="${CSS.escape(partId)}"]`)),
      ...Array.from(document.querySelectorAll("[data-overlay-keep]")),
    ];
    const inertMarks = root ? markInert(collectInertTargets(keep)) : [];

    function handleKeyDown(event: KeyboardEvent) {
      // Only the topmost overlay reacts to keyboard input.
      if (overlayStack[overlayStack.length - 1] !== overlayId) return;
      if (event.key === "Escape") {
        event.preventDefault();
        onOpenChangeRef.current?.(false);
        return;
      }
      if (event.key !== "Tab") return;
      const currentRoot = rootRef.current;
      if (!currentRoot) return;
      const focusables = getFocusableElements(currentRoot);
      if (focusables.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (event.shiftKey) {
        if (active === first || !currentRoot.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !currentRoot.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      stopFocus();
      document.removeEventListener("keydown", handleKeyDown);
      overlayStack.splice(overlayStack.indexOf(overlayId), 1);
      unlockBodyScroll();
      // Release before restoring focus: an inert element cannot take focus.
      releaseInert(inertMarks);
      // preventScroll: the trigger may be the sticky tab bar's «Más», whose
      // static spot is the end of the page; focusing it after a navigation
      // scrolled the new screen's title away (shell-sec, Actividad at 360).
      if (previouslyFocused?.isConnected) previouslyFocused.focus({ preventScroll: true });
    };
  }, [open, partId]);

  return { rootRef, partId };
}
